/**
 * "Merlin found answers" (kanban 3eed0be5): the candidates the daily sweep
 * thinks are already answered, each with the answer, the evidence and two
 * one-click choices — Confirm (close the item with that answer) or Still
 * open — plus a quiet fold of what the sweep closed on its own this week.
 * Nothing here writes to anyone: Confirm records the answer on the item.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { trackEvent } from '../../services/portalTracking';
import {
  confirmFoundAnswer,
  declineFoundAnswer,
  type FoundAnswerRead,
} from '../../services/qcpFoundAnswers';
import {
  confidenceWord,
  evidenceLine,
  kindWord,
  placeWord,
  questionTitle,
  retiredByWord,
  splitFoundAnswers,
} from './foundAnswersView';
import styles from './FoundAnswers.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

export interface FoundAnswersProps {
  rows: FoundAnswerRead[];
  /** Called with the updated row after Confirm / Still open went through. */
  onChanged: (updated: FoundAnswerRead) => void;
  /** How many to show before "Show N more" (UX round 2026-10-27: 17 of
   *  them filled the first screen of Triage, above the one item that
   *  needed a decision). */
  foldAt?: number;
}

export function FoundAnswers({ rows, onChanged, foldAt = 3 }: FoundAnswersProps) {
  // A read-only viewer sees what Merlin found; the desk rules on it (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const { candidates, closed } = splitFoundAnswers(rows);
  if (candidates.length === 0 && closed.length === 0) return null;
  const folded = candidates.length - foldAt;
  const shown = showAll || folded <= 0 ? candidates : candidates.slice(0, foldAt);

  const act = async (
    row: FoundAnswerRead,
    label: 'qcp_found_answer_confirm' | 'qcp_found_answer_decline',
    work: () => Promise<FoundAnswerRead>,
  ) => {
    if (busyId) return;
    setBusyId(row.id);
    setError(null);
    try {
      const updated = await work();
      trackEvent('action', label, {
        answer_sweep_item_id: row.id,
        item_kind: row.item_kind,
        item_id: row.item_id,
        thread_no: row.thread_no || null,
        investigation_no: row.investigation_no || null,
        result: 'request_sent',
      });
      onChanged(updated);
    } catch {
      setError('That did not go through. Try again in a moment.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className={styles.box} aria-labelledby="found-answers" data-testid="found-answers">
      <h2 id="found-answers" className={styles.head}>
        Merlin found answers
        {candidates.length > 0 && (
          <span className={styles.count} data-testid="found-answers-count">
            {candidates.length} to confirm
          </span>
        )}
      </h2>
      {candidates.length > 0 && (
        <ul className={styles.list}>
          {shown.map((row) => {
            const busy = busyId === row.id;
            const place = placeWord(row);
            return (
              <li key={row.id} className={styles.row} data-testid="found-answer-row">
                <div className={styles.text}>
                  <div className={styles.line}>
                    The {kindWord(row)} <em>“{questionTitle(row)}”</em>
                    {row.person_name && <> to <strong>{row.person_name}</strong></>}
                    {place && (
                      <>
                        {' '}on <Link className={styles.place} to={row.link.replace(/^\/portal\/quality-copilot\/?/, '')}>{place}</Link>
                      </>
                    )}{' '}
                    looks answered
                    {row.verdict === 'unsure' ? ' (Merlin is not sure)' : confidenceWord(row.confidence) ? ` (${confidenceWord(row.confidence)} sure)` : ''}:
                  </div>
                  {row.answer_summary && <p className={styles.answer}>{row.answer_summary}</p>}
                  {row.evidence.length > 0 && (
                    <ul className={styles.evidence} aria-label="Evidence">
                      {row.evidence.slice(0, 4).map((e) => (
                        <li key={e.id}>{evidenceLine(e)}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {!readOnly && (<div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.confirm}
                    disabled={busy}
                    onClick={() => void act(row, 'qcp_found_answer_confirm', () => confirmFoundAnswer(row.id))}
                  >
                    Yes, that answers it
                  </button>
                  <button
                    type="button"
                    className={styles.decline}
                    disabled={busy}
                    onClick={() => void act(row, 'qcp_found_answer_decline', () => declineFoundAnswer(row.id))}
                  >
                    Still open
                  </button>
                </div>)}
              </li>
            );
          })}
        </ul>
      )}
      {folded > 0 && (
        <button
          type="button"
          className={styles.fold}
          aria-expanded={showAll}
          data-testid="found-answers-more"
          onClick={() => {
            trackEvent('action', 'qcp_found_answers_more_toggle', {
              open: !showAll,
              hidden: folded,
              result: 'panel_toggled',
            });
            setShowAll((v) => !v);
          }}
        >
          {showAll ? 'Show fewer' : `Show ${folded} more to confirm`}
        </button>
      )}
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {closed.length > 0 && (
        <div className={styles.closedWrap}>
          <button
            type="button"
            className={styles.fold}
            aria-expanded={showClosed}
            onClick={() => {
              trackEvent('action', 'qcp_found_answers_closed_toggle', { open: !showClosed, result: 'panel_toggled' });
              setShowClosed((v) => !v);
            }}
          >
            {showClosed ? 'Hide' : 'Show'} {closed.length} closed this week
          </button>
          {showClosed && (
            <ul className={styles.closed} data-testid="found-answers-closed">
              {closed.map((row) => (
                <li key={row.id}>
                  <em>“{questionTitle(row)}”</em>
                  {placeWord(row) && <> on {placeWord(row)}</>} — closed by {retiredByWord(row)}
                  {row.answer_summary && <>: {row.answer_summary}</>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

export default FoundAnswers;
